import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, Scissors, Sparkles, AlertTriangle, RotateCw } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useLang } from "../../context/LanguageContext";
import { frameAt } from "./ClipCard";

// Full-screen loader: downloads the exact AI-trimmed segment, then hands the ready asset to the editor.
export const ClipPrepOverlay = ({ clip, sb, onReady, onCancel }) => {
  const { L } = useLang();
  const [step, setStep] = useState(0);
  const [pct, setPct] = useState(4);
  const [err, setErr] = useState(null);
  const [run, setRun] = useState(0);
  const alive = useRef(true);
  const steps = [L("Conectando con YouTube", "Connecting to YouTube"), L("Recortando el momento elegido por la IA", "Cutting the AI-picked moment"), L("Preparando el editor", "Preparing the editor")];

  useEffect(() => {
    alive.current = true; setErr(null); setStep(0); setPct(4);
    const tick = setInterval(() => setPct((p) => (p < 95 ? p + Math.max(0.3, (95 - p) / 60) : p)), 300);
    // AI emojis are prepared while the clip downloads, so they're already there when the editor opens
    const lines = [...new Set((clip.captions || []).map((c) => c.text))];
    const emoP = lines.length ? api.post("/clips/emojis", { lines, context: clip.title })
      .then(({ data }) => Object.fromEntries(lines.map((l, i) => [l, data.lines[i]]).filter(([, v]) => v))).catch(() => null) : Promise.resolve(null);
    (async () => {
      try {
        const { data: job } = await api.post("/editor/import-clip", { video_id: clip.videoId, start: clip.start, end: clip.end, title: clip.title });
        let asset = null;
        const floor = { download: 10, encode: 60, upload: 85 };
        for (let i = 0; i < 150 && !asset && alive.current; i++) {
          if (i) await new Promise((r) => setTimeout(r, 1200));
          const { data: st } = await api.get(`/editor/import-clip/${job.job_id}`);
          if (st.stage === "encode") setStep(1);
          if (st.stage === "upload") setStep(2);
          if (floor[st.stage]) setPct((p) => Math.max(p, floor[st.stage]));
          if (st.status === "done") asset = st.asset;
          else if (st.status === "error") throw new Error(st.error);
        }
        if (!asset) throw new Error("timeout");
        setStep(2);
        const res = await api.get(`/media/${asset.id}`, { responseType: "blob" });
        if (!alive.current) return;
        setPct(100);
        if (asset.preview) toast.warning(L("YouTube bloqueó el vídeo original: hemos colocado una versión de vista previa (sin audio) para que puedas editar igualmente.", "YouTube blocked the original video: we placed a preview version (no audio) so you can still edit."), { duration: 8000 });
        const emoMap = await Promise.race([emoP, new Promise((r) => setTimeout(() => r(null), 8000))]);
        setTimeout(() => alive.current && onReady({ ...asset, emoMap, localUrl: URL.createObjectURL(res.data) }), 450);
      } catch (e) { if (alive.current) setErr(e?.response?.status === 401 ? "auth" : "fail"); }
      finally { clearInterval(tick); }
    })();
    return () => { alive.current = false; clearInterval(tick); };
    // eslint-disable-next-line
  }, [run]);

  const cover = frameAt(sb, (clip.start + clip.end) / 2);
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md grid place-items-center p-4" data-testid="clip-prep-overlay">
      <motion.div initial={{ opacity: 0, scale: 0.94, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="relative w-full max-w-md rounded-3xl border border-white/10 bg-zinc-950 overflow-hidden shadow-[0_40px_120px_-30px_rgba(250,204,21,.45)]">
        <div className="relative aspect-video overflow-hidden">
          <div className="absolute inset-0" style={cover || { backgroundImage: `url(${clip.thumb})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          {clip.videoId?.length === 11 && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/clips/cover?video_id=${clip.videoId}&t=${Math.floor((clip.start + clip.end) / 2)}`} alt="" data-testid="prep-hq-cover"
            onLoad={(e) => { e.currentTarget.style.opacity = 1; }} className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-700" />}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-black/30 to-black/20" />
          {!err && <motion.div className="absolute top-0 bottom-0 w-24 bg-gradient-to-r from-transparent via-yellow-400/35 to-transparent" animate={{ left: ["-25%", "110%"] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }} />}
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-yellow-400 px-2.5 py-1 text-[11px] font-black"><Sparkles className="h-3 w-3" />CLIP IA · {clip.score}%</span>
          <span className="absolute top-3 right-3 rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-mono text-white">{clip.startLabel} → {clip.endLabel}</span>
          {clip.hook && <p className="absolute bottom-3 inset-x-4 text-center font-black uppercase text-yellow-300 text-sm" style={{ textShadow: "0 2px 8px #000" }}>{clip.hook}</p>}
        </div>
        <div className="p-6">
          <p className="font-extrabold text-lg text-white line-clamp-1">{clip.title}</p>
          {!err ? (<>
            <div className="mt-5 space-y-3">
              {steps.map((s, i) => (
                <div key={s} className="flex items-center gap-3 text-sm" data-testid={`prep-step-${i}`}>
                  {i < step ? <span className="h-5 w-5 rounded-full bg-yellow-400 grid place-items-center"><Check className="h-3 w-3" strokeWidth={3} /></span>
                    : i === step ? <Loader2 className="h-5 w-5 text-yellow-400 animate-spin" /> : <span className="h-5 w-5 rounded-full border border-white/15" />}
                  <span className={i <= step ? "text-white font-medium" : "text-zinc-500"}>{s}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden"><motion.div className="h-full rounded-full bg-gradient-to-r from-amber-500 to-yellow-300" animate={{ width: `${pct}%` }} transition={{ ease: "easeOut" }} /></div>
              <span className="text-xs font-mono text-zinc-400 w-9 text-right" data-testid="prep-percent">{Math.round(pct)}%</span>
            </div>
            <button onClick={onCancel} data-testid="prep-cancel" className="mt-5 text-xs text-zinc-500 hover:text-white">{L("Cancelar", "Cancel")}</button>
          </>) : (
            <div className="mt-4" data-testid="prep-error">
              <p className="flex items-start gap-2 text-sm text-zinc-300"><AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />{err === "auth" ? L("Inicia sesión para editar este clip.", "Sign in to edit this clip.") : L("YouTube no dejó descargar el clip ahora mismo. Reinténtalo en unos segundos.", "YouTube blocked the clip download right now. Try again in a few seconds.")}</p>
              <div className="mt-5 flex gap-2">
                <button onClick={() => setRun((r) => r + 1)} data-testid="prep-retry" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 py-2.5 text-sm font-extrabold"><RotateCw className="h-4 w-4" />{L("Reintentar", "Retry")}</button>
                <button onClick={() => onReady(null)} data-testid="prep-open-anyway" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/15 py-2.5 text-sm font-semibold text-white hover:bg-white/5"><Scissors className="h-4 w-4" />{L("Abrir editor", "Open editor")}</button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};
